<template>
  <aside
    class="menu is-paddingless"
    data-unit="toc-3d-content"
  >
    <div
      v-if="!hasQuery"
      class="buttons has-addons toc-expand-collapse"
    >
      <button
        class="button"
        type="button"
        @click="setAllExpanded(true)"
      >
        {{ $t('expandAll') }}
      </button>
      <button
        class="button"
        type="button"
        @click="setAllExpanded(false)"
      >
        {{ $t('collapseAll') }}
      </button>
    </div>
    <ul class="menu-list">
      <li
        v-for="model in filteredModels"
        :key="model.id"
        data-unit="toc-3d-model"
      >
        <div class="toc-row toc-model-row">
          <button
            class="button is-white expand-toggle"
            type="button"
            :aria-expanded="isExpanded(model.id)"
            :aria-label="isExpanded(model.id) ? $t('collapse') : $t('expand')"
            @click.stop="toggleExpanded(model.id)"
          >
            <span class="icon is-small">
              <i
                aria-hidden="true"
                :class="isExpanded(model.id) ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"
              />
            </span>
          </button>
          <button
            class="button is-white toc-label"
            :class="{ 'is-active': isModelActive(model) }"
            type="button"
            @click="selectModel(model)"
          >
            <span class="toc-item-text">
              <span
                v-if="getPresentation(model.searchResult).highlightedNameHtml"
                class="toc-item-highlight"
                v-html="sanitizeHtml(getPresentation(model.searchResult).highlightedNameHtml || '')"
              />
              <span v-else>{{ model.name }}</span>
              <span
                v-if="getPresentation(model.searchResult).helpTextHtml"
                class="help-text toc-item-highlight"
                v-html="sanitizeHtml(getPresentation(model.searchResult).helpTextHtml || '')"
              />
              <span
                v-else-if="getPresentation(model.searchResult).helpTextKey"
                class="help-text"
              >
                {{ $tc(
                  getPresentation(model.searchResult).helpTextKey,
                  getPresentation(model.searchResult).helpTextParams?.count || 1,
                  getPresentation(model.searchResult).helpTextParams
                ) }}
              </span>
            </span>
          </button>
        </div>
        <ul v-show="isExpanded(model.id)">
          <li
            v-for="snapshot in model.snapshots"
            :key="snapshot.id"
          >
            <button
              class="button is-white toc-row snapshot-row"
              :class="{ 'is-active': isSnapshotActive(snapshot) }"
              type="button"
              @click="selectSnapshot(snapshot)"
            >
              <span class="toc-item-text">
                <span
                  v-if="getPresentation(snapshot.searchResult).highlightedNameHtml"
                  class="toc-item-highlight"
                  v-html="sanitizeHtml(getPresentation(snapshot.searchResult).highlightedNameHtml || '')"
                />
                <span v-else>{{ snapshot.snapshotName || snapshot.id }}</span>
                <span
                  v-if="getPresentation(snapshot.searchResult).helpTextHtml"
                  class="help-text toc-item-highlight"
                  v-html="sanitizeHtml(getPresentation(snapshot.searchResult).helpTextHtml || '')"
                />
                <span
                  v-else-if="getPresentation(snapshot.searchResult).helpTextKey"
                  class="help-text"
                >
                  {{ $tc(
                    getPresentation(snapshot.searchResult).helpTextKey,
                    getPresentation(snapshot.searchResult).helpTextParams?.count || 1,
                    getPresentation(snapshot.searchResult).helpTextParams
                  ) }}
                </span>
              </span>
            </button>
          </li>
        </ul>
      </li>
    </ul>
    <p
      v-if="hasQuery && !filteredModels.length"
      aria-live="polite"
      class="has-text-grey has-text-centered search-status"
      role="status"
    >
      {{ $t('noSearchResultsHelpText') }}
    </p>
  </aside>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { useStore } from 'vuex'
import i18n from '@/locales'
import { sanitizeHtml } from '@/helpers/sanitizeHtml'
import {
  getThreeDHighlightedContent,
  getThreeDTocSearchResultKey
} from '@/helpers/threeDTocSearch'
import type { ThreeDTocSearchResult } from '@/interfaces/library'

const store = useStore()
const emit = defineEmits(['select'])
const props = defineProps({
  beforeSelect: {
    default: undefined,
    type: Function as PropType<() => Promise<void> | void>
  }
})
type SearchableSnapshot = Record<string, unknown> & {
  id: number
  searchResult?: ThreeDTocSearchResult | null
}

type SearchableModel = Record<string, unknown> & {
  id: number
  name: string
  searchResult?: ThreeDTocSearchResult | null
  snapshots: SearchableSnapshot[]
}

const models = computed<SearchableModel[]>(() => store.getters['content/threeDModels'])
const expandedModelIds = computed(() => store.state.content.toc.expandedThreeDModelIds)
const selectedModelId = computed(() => store.state.content.toc.selectedThreeDModelId)
const selectedSearchResultKey = computed(() =>
  store.state.content.toc.selectedThreeDSearchResultKey || ''
)
const selectedSnapshotId = computed(() => store.state.content.toc.selectedThreeDSnapshotId)
const searchResults = computed<ThreeDTocSearchResult[]>(() =>
  store.state.content.toc.searchResults ?? []
)
const tocQuery = computed(() => {
  const query = store.state.content.toc.query
  return typeof query === 'string' ? query : ''
})
const hasQuery = computed(() => Boolean(tocQuery.value.trim()))
const locale = computed(() => {
  const currentLocale = i18n.global.locale
  return typeof currentLocale === 'object' && 'value' in currentLocale
    ? currentLocale.value
    : currentLocale
})
const resultsByKey = computed(() => new Map(
  searchResults.value
    .map(result => [getThreeDTocSearchResultKey(result), result] as const)
    .filter(([key]) => Boolean(key))
))
const filteredModels = computed<SearchableModel[]>(() => {
  if (!hasQuery.value) return models.value

  return models.value.reduce<SearchableModel[]>((filtered, model) => {
    const modelResult = resultsByKey.value.get(`model:${String(model.id)}`) || null
    const snapshots = model.snapshots.reduce<SearchableSnapshot[]>((matches, snapshot) => {
      const searchResult = resultsByKey.value.get(`snapshot:${String(snapshot.id)}`) || null
      if (modelResult || searchResult) matches.push({ ...snapshot, searchResult })
      return matches
    }, [])

    if (modelResult || snapshots.length) {
      filtered.push({ ...model, searchResult: modelResult, snapshots })
    }
    return filtered
  }, [])
})

const isExpanded = (modelId: number) => hasQuery.value || expandedModelIds.value.includes(modelId)
const isSearchResultActive = (result?: ThreeDTocSearchResult | null) => {
  const key = getThreeDTocSearchResultKey(result)
  return Boolean(key) && key === selectedSearchResultKey.value
}
const isModelActive = (model: SearchableModel) => selectedSearchResultKey.value
  ? isSearchResultActive(model.searchResult)
  : model.id === selectedModelId.value
const isSnapshotActive = (snapshot: SearchableSnapshot) => selectedSearchResultKey.value
  ? isSearchResultActive(snapshot.searchResult)
  : snapshot.id === selectedSnapshotId.value
const getPresentation = (result?: ThreeDTocSearchResult | null) =>
  getThreeDHighlightedContent(result, locale.value)
const selectModel = async (model: SearchableModel) => {
  await props.beforeSelect?.()
  if (model.searchResult) {
    await store.dispatch('content/selectThreeDTocSearchResult', {
      searchResult: model.searchResult
    })
  } else {
    await store.dispatch('content/selectThreeDModel', model.id)
  }
  emit('select')
}
const selectSnapshot = async (snapshot: SearchableSnapshot) => {
  await props.beforeSelect?.()
  if (snapshot.searchResult) {
    await store.dispatch('content/selectThreeDTocSearchResult', {
      searchResult: snapshot.searchResult
    })
  } else {
    await store.dispatch('content/selectThreeDSnapshot', snapshot)
  }
  emit('select')
}
const toggleExpanded = (modelId: number) => {
  if (!hasQuery.value) store.dispatch('content/toggleThreeDModelExpanded', modelId)
}
const setAllExpanded = (expanded: boolean) => store.dispatch('content/setAllThreeDModelsExpanded', expanded)
</script>

<style scoped>
.toc-expand-collapse {
  justify-content: center;
  margin: 0 0 0.75rem;
}
.toc-row {
  align-items: center;
  display: flex;
  text-align: left;
  width: 100%;
}
.toc-model-row {
  padding-right: 0.75em;
}
.toc-row.is-active,
.toc-label.is-active {
  background: var(--primary);
  color: var(--primary-invert);
}
.expand-toggle {
  flex: 0 0 auto;
}
.toc-label {
  flex: 1;
  height: auto;
  justify-content: flex-start;
  padding-left: 0.25rem;
  padding-right: 0;
  text-align: left;
}
.snapshot-row {
  height: auto;
  justify-content: flex-start;
  padding: 0.5em 0.75em 0.5em 2.75em;
  white-space: normal;
}
.toc-item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
}
.toc-item-highlight :deep(.highlightText) {
  text-decoration: underline;
}
.help-text {
  display: block;
  font-size: small;
  font-style: oblique;
  white-space: normal;
}
.search-status {
  padding: 1rem;
}
</style>
